import React from "react";
import { Link } from "react-router-dom";
import aashsihcv from "../assets/aashishcv2.pdf";
import profileImage from "../assets/aashishcollege.jpg";

const facts = [
  ["Based in", "Pokhara, Nepal"],
  ["Experience", "3+ years"],
  ["Focus", "Web scraping, backend APIs, ETL"],
  ["Languages", "Python, Node.js"],
  ["Scraping", "Scrapy, Playwright, Puppeteer"],
  ["Databases", "PostgreSQL, MongoDB, MySQL"],
];

const chapters = [
  {
    title: "Web scraping & data extraction",
    text: "My core expertise is web scraping services: building reliable crawlers and scrapers using Scrapy, Playwright, Puppeteer, and Cheerio. I handle JavaScript-rendered pages, anti-bot bypass, proxy rotation, and large-scale data collection for eCommerce platforms, business directories, SERP tracking, and lead generation pipelines in Nepal and globally.",
  },
  {
    title: "Backend development",
    text: "Beyond scraping, I architect the backend systems that store, process, and serve extracted data. I build RESTful APIs with Node.js, Python and Express, design PostgreSQL, MongoDB and MySQL schemas optimized for large datasets, and implement cron-based scheduling to automate recurring scraping tasks.",
  },
  {
    title: "Automation & ETL pipelines",
    text: "I design end-to-end ETL pipelines that extract data from any web source, transform and clean it, and load it into structured databases or deliver it via API. From one-time bulk extractions to fully automated scheduled pipelines, I build solutions that run reliably without manual effort.",
  },
  {
    title: "Where I'm heading",
    text: "I'm continuously advancing my expertise in distributed scraping infrastructure, headless browser optimization, and AI-assisted data extraction. My goal is to deliver faster, more resilient web scraping solutions for businesses across Nepal and beyond.",
  },
];

const AboutDescribe = () => {
  return (
    <section className="py-20 lg:py-24 bg-dark-900/40 text-light-100">
      <div className="container mx-auto px-6">
        <header className="max-w-3xl mb-16">
          <p className="font-code text-sm text-primary-400 mb-3">About</p>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
            Web Scraper &amp; Backend Developer in Nepal
          </h1>
          <p className="text-xl text-gray-300">
            I'm a professional web scraper and backend developer based in
            Pokhara, Nepal. With 3+ years of hands-on experience, I build
            automated data extraction systems and server-side applications that
            help businesses make smarter, data-driven decisions.
          </p>
        </header>

        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <img
                src={profileImage}
                alt="Aashish Timsina, web scraper and backend developer in Pokhara, Nepal"
                width="512"
                height="512"
                loading="lazy"
                className="w-full rounded-lg border border-dark-600 object-cover aspect-[4/5]"
              />

              <dl className="glass-card divide-y divide-dark-600">
                {facts.map(([label, value]) => (
                  <div
                    key={label}
                    className="grid grid-cols-3 gap-3 px-5 py-3 text-sm"
                  >
                    <dt className="font-code text-gray-500">{label}</dt>
                    <dd className="col-span-2">{value}</dd>
                  </div>
                ))}
              </dl>

              <div className="flex flex-wrap gap-4 items-center">
                <Link
                  to="/contact"
                  className="px-5 py-2.5 bg-primary-500 text-dark-900 rounded-md font-semibold hover:bg-primary-400 transition-colors"
                >
                  Hire me
                </Link>
                <a
                  href={aashsihcv}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-4 decoration-dark-500 hover:decoration-primary-400 transition-colors"
                >
                  Download CV
                </a>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <ol className="space-y-12">
              {chapters.map((chapter, i) => (
                <li
                  key={chapter.title}
                  className="grid gap-4 md:grid-cols-12 md:gap-8 border-t border-dark-600 pt-8"
                >
                  <span className="md:col-span-2 font-code text-sm text-amber-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="md:col-span-10">
                    <h2 className="text-2xl font-semibold mb-3">
                      {chapter.title}
                    </h2>
                    <p className="text-gray-300 text-lg">{chapter.text}</p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-16 glass-card p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <div>
                <h2 className="text-xl font-semibold mb-1">
                  Have a data project in mind?
                </h2>
                <p className="text-gray-400">
                  Tell me what you need to collect and how often.
                </p>
              </div>
              <Link
                to="/contact"
                className="shrink-0 px-6 py-3 bg-primary-500 text-dark-900 rounded-md font-semibold hover:bg-primary-400 transition-colors text-center"
              >
                Start a conversation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutDescribe;
